import { useCallback, useEffect, useState } from "react";
import { api, normalizeCode, SESSION_KEY } from "./api";
import { Home } from "./views/Home";
import { PairConfirm } from "./views/PairConfirm";
import { Session } from "./views/Session";

type Route =
  | { view: "boot" }
  | { view: "home" }
  | {
      view: "session";
      code: string;
      showQr?: boolean;
      showScanner?: boolean;
    }
  | { view: "pair"; token: string };

/**
 * Nach dem Beitritt per QR-Scan auf dem Handy direkt den Scanner öffnen,
 * damit gleich der Pairing-QR des nächsten Rechners gescannt werden kann.
 * Nur sinnvoll auf Touch-Geräten mit verfügbarer Kamera.
 */
function canAutoScan(): boolean {
  return (
    window.matchMedia("(pointer: coarse)").matches &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

export function App() {
  const [route, setRoute] = useState<Route>({ view: "boot" });
  const [notice, setNotice] = useState<string | null>(null);

  const enterSession = useCallback(
    (code: string, options?: { showQr?: boolean; showScanner?: boolean }) => {
      localStorage.setItem(SESSION_KEY, code);
      setNotice(null);
      setRoute({ view: "session", code, ...options });
    },
    [],
  );

  const leaveSession = useCallback((message?: string) => {
    localStorage.removeItem(SESSION_KEY);
    setNotice(message ?? null);
    setRoute({ view: "home" });
  }, []);

  useEffect(() => {
    void (async () => {
      const pairMatch = location.pathname.match(/^\/pair\/([\w-]+)\/?$/);
      if (pairMatch?.[1]) {
        setRoute({ view: "pair", token: pairMatch[1] });
        return;
      }

      // /j/CODE – Beitritts-Link aus dem QR-Code
      const joinMatch = location.pathname.match(/^\/j\/([\w-]+)\/?$/);
      if (joinMatch?.[1]) {
        history.replaceState(null, "", "/");
        const code = normalizeCode(joinMatch[1]);
        try {
          await api.getSession(code);
          enterSession(code, { showScanner: canAutoScan() });
        } catch {
          setNotice("Session nicht gefunden oder abgelaufen.");
          setRoute({ view: "home" });
        }
        return;
      }

      // Gemerkte Session wiederherstellen
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        try {
          await api.getSession(stored);
          setRoute({ view: "session", code: stored });
          return;
        } catch {
          localStorage.removeItem(SESSION_KEY);
        }
      }
      setRoute({ view: "home" });
    })();
  }, [enterSession]);

  switch (route.view) {
    case "boot":
      return <div className="boot">Lädt …</div>;
    case "home":
      return <Home notice={notice} onEnter={enterSession} />;
    case "session":
      return (
        <Session
          code={route.code}
          initialShowQr={route.showQr ?? false}
          initialShowScanner={route.showScanner ?? false}
          onLeave={leaveSession}
        />
      );
    case "pair":
      return (
        <PairConfirm
          token={route.token}
          onFinished={(code, options) => {
            history.replaceState(null, "", "/");
            if (code) {
              setRoute({
                view: "session",
                code,
                showScanner: (options?.showScanner ?? false) && canAutoScan(),
              });
            } else {
              setRoute({ view: "home" });
            }
          }}
        />
      );
  }
}
